import { Component, OnInit, OnDestroy } from "@angular/core";

import { FormArray, FormGroup } from "@angular/forms";

import { ActivatedRoute } from "@angular/router";

import { MatDialog, MatDialogRef } from "@angular/material/dialog";

import { Subscription, interval, Subject } from "rxjs";

import { filter } from "rxjs/operators";
import { takeUntil } from "rxjs/operators";

import { FormlyFormOptions, FormlyFieldConfig } from "@ngx-formly/core";

import { environment } from "@environment/environment";

import { MessageService } from "@shared/services/message.service";
import { QuestionnaireService } from "@shared/services/questionnaire.service";
import { ConfirmDialogService } from "@app/shared/components/dialogs/confirm-dialog/confirm-dialog.service";

import { Question } from "@shared/models/question";
import { Guid } from "@app/shared/models/guid";
import { DataGridConfig } from "@shared/components/data-grid/data-grid-config";

import { TabDialogComponent } from "./dialogs/tab-dialog/tab-dialog.component";
import { QuestionDialogComponent } from "./dialogs/question-dialog/question-dialog.component";
import { InsertPosition } from "./dialogs/question-dialog/interfaces/insert-position.interface";
import { LinkRendererComponent } from "@shared/components/cell-renderers/link-renderer/link-renderer.component";
import { QuestionnaireAddTemplateDialogComponent } from "./dialogs/questionnaire-add-template-dialog/questionnaire-add-template-dialog.component";
import { GuidanceNotesService } from "@shared/services/guidanceNotes.service";
import { LocalStorageService } from "@app/shared/services/local-storage-service";
import { UserSubscriber } from "@app/shared/subscribers/user.subscriber";
import { IUser, User } from "@app/shared/models/user";

export interface TabType {
  label: string;
  fields: FormlyFieldConfig[];
}

@Component({
  selector: "db-edit-questionnaire",
  templateUrl: "./questionnaire.component.html",
  styleUrls: ["./questionnaire.component.scss"],
})
export class QuestionnaireComponent implements OnInit, OnDestroy {
  selectedTabIndex: number;
  questionDialogRef: MatDialogRef<QuestionDialogComponent>;
  tabDialog: MatDialogRef<TabDialogComponent>;
  addTemplateDialogRef: MatDialogRef<QuestionnaireAddTemplateDialogComponent>;
  sessionID: any = 0;
  clientViewUrl: string;
  subscription: Subscription;
  subscriptionManager = new Subscription();
  user: IUser;
  model: any = {};
  tabs: TabType[] = [];
  hasLoaded: any;
  form = new FormArray([]);
  options: FormlyFormOptions[] = [];
  draftDocumentRowData: any[];
  draftDocumentGridConfig: DataGridConfig;
  CurrentSectors: string;
  private id: string;
  private onDestroy$ = new Subject<void>();

  constructor(
    private questionnaireService: QuestionnaireService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private confirmDialogService: ConfirmDialogService,
    private guidanceNotesService: GuidanceNotesService,
    private messageService: MessageService,
    private _localStorageService: LocalStorageService,
    private _userSubscriber: UserSubscriber,
  ) {
    this.sessionID = 123;
    this.id = this.route.snapshot.paramMap.get("id");
    this.hasLoaded = false;
    this.subscription = this.messageService
      .getMessage()
      .subscribe((message) => {
        if (message.text.startsWith("delete")) {
          this.confirmDialogService
            .confirm(
              "Confirm",
              "Are you sure you want to delete the selected documents?",
              "Confirm",
            )
            .subscribe((result) => {
              if (result) {
                message.text = message.text.replace("delete:", "");
                let idToDelete;

                this.questionnaireService
                  .getQuestionnaireContent(this.id)
                  .subscribe(([model, fields]) => {
                    for (const t of fields.body) {
                      for (const f of t.fields) {
                        if (
                          f.key === message.text ||
                          (f.fieldGroup &&
                            f.fieldGroup.find((i) => i.id === message.text))
                        ) {
                          idToDelete = f.id;
                        }
                      }
                    }
                    for (let i = 0; i < fields.body.length; i++) {
                      fields.body[i].fields = fields.body[i].fields.filter(
                        (it) => it.id !== idToDelete,
                      );
                    }

                    console.log("cleansed=" + fields);

                    this.questionnaireService
                      .saveQuestionSet(fields, this.model, this.id)
                      .subscribe(() => {
                        this.tabs = fields.body;
                        this.model = model;
                      });
                  });
              }
            });
        } else if (message.text.startsWith("insert:")) {
          this.openInsertQuestionDialog(message.text.replace("insert:", ""));
        } else {
          this.questionnaireService
            .getQuestionnaireContent(this.id)
            .subscribe(([model, fields]) => {
              for (const t of fields.body) {
                for (const f of t.fields) {
                  if (
                    f.id === message.text ||
                    f.key === message.text ||
                    (f.fieldGroup &&
                      f.fieldGroup.find((i) => i.id === message.text))
                  ) {
                    const question = new Question(f);
                    question.value = model[question.key];
                    question.newField = false;
                    this.openQuestionDialog(t.label, question);
                    return false;
                  }
                }
              }
            });
        }
      });

    this.questionnaireService
      .getQuestionnaireContent(this.id)
      .subscribe(([model, fields]) => {
        this.model = model;
        this.tabs = fields.body;

        this.syncFormsWithTabs();

        this.CurrentSectors = this.questionnaireService.getSector(
          this.model.hideExpressionValue,
        );

        this.updateSessionID();
      });
  }

  get isAllPristine(): boolean {
    return this.form.pristine;
  }

  private syncFormsWithTabs(): void {
    while (this.form.length < this.tabs.length) {
      this.form.push(new FormGroup({}));
      this.options.push(<FormlyFormOptions>{});
    }

    while (this.form.length > this.tabs.length) {
      this.form.removeAt(this.form.length - 1);
      this.options.pop();
    }
  }

  ngOnInit() {
    this.initializeDraftDocumentRowData();
    this.configureDraftDocumentDataGrid();
    this.subscriptionManager.add(this.subscribeToUser());

    interval(environment.autoSaveTimout)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((x) => {
        if (this.form.dirty) {
          console.log("auto saving quetionnaire silently @ " + new Date());
          this.questionnaireService.saveQuestionnaire(
            this.model,
            this.id,
            true,
          );
          this.form.markAsPristine();
        }
      });
  }

  private subscribeToUser(): Subscription {
    return this._userSubscriber.user$.subscribe((user: User) => {
      this.user = user;
    });
  }

  AddTextField(tab: TabType, insertAt?: InsertPosition) {
    const question = new Question();
    const regx = /[-;\\\\/:*?\"<>|&, ']/g;

    let uniqueId = "XX_" + Guid.newGuid();
    uniqueId = uniqueId.replace(regx, "_");
    question.key = uniqueId;
    question.id = uniqueId;
    this.openQuestionDialog(tab, question, insertAt);
  }

  openInsertQuestionDialog(fieldId: string) {
    const tab = this.tabs.find((t) =>
      t.fields.some((f) => this.isFieldMatch(f, fieldId)),
    );
    if (!tab) {
      return;
    }

    const anchor = tab.fields.find((f) => this.isFieldMatch(f, fieldId));
    this.AddTextField(tab, {
      position: "after",
      anchorId: anchor.id as string,
    });
  }

  private isFieldMatch(field: FormlyFieldConfig, fieldId: string): boolean {
    return (
      field.id === fieldId ||
      field.key === fieldId ||
      (!!field.fieldGroup && field.fieldGroup.some((i) => i.id === fieldId))
    );
  }

  openQuestionDialog(tname, question?, insertAt?: InsertPosition) {
    this.questionDialogRef = this.dialog.open(QuestionDialogComponent, {
      width: "1090px",
      panelClass: "db-dialog",
      data: {
        tabs: this.tabs,
        tabLabel: typeof tname === "string" ? tname : tname?.label,
        question: question,
        insertAt: insertAt,
      },
    });

    this.questionDialogRef
      .afterClosed()
      .pipe()
      .subscribe((returnQuestion) => {
        if (!returnQuestion) return false;

        const {
          question,
          action,
          insertAt: selectedInsertAt,
        } = JSON.parse(returnQuestion);
        if (action !== "save") return false;

        if (question.newField) {
          this.AddField(tname, question, selectedInsertAt);
        } else {
          this.EditField(tname, question);
        }
      });

    return false;
  }

  submit() {
    if (this.form.dirty) {
      this.questionnaireService.saveQuestionnaire(this.model, this.id);
      this.form.markAsPristine();
    }
  }

  updateSessionID() {
    this.sessionID = this.generateRandomSessionID();
    this.guidanceNotesService.getSession(this.sessionID).subscribe({
      next: (event) => this.addSessionToModel(event),
      error: (err) => this.addSessionToModel,
    });
  }

  addSessionToModel(event) {
    if (event) {
      this.updateSessionID();
    } else {
      let theme = this._localStorageService.getItem("theme");
      if (theme === "Croner") {
        theme = "croner";
      } else if (theme === "Peninsula") {
        theme = "peninsula";
      } else if (theme === "BrightHR") {
        theme = "bright";
      } else {
        theme = "peninsula";
      }
      this.clientViewUrl =
        environment.videoDocBuilderGuidanceViewer +
        this.sessionID +
        "&department=" +
        this.user.selectedDepartment;
      this.model["sessionID"] = this.sessionID;
    }
    this.model["questionnaireId"] = this.id;
    this.hasLoaded = true;
  }

  generateRandomSessionID() {
    return Math.floor(Math.random() * Math.floor(9999));
  }

  formChanged(): boolean {
    const valuesChanged = false;

    return valuesChanged;
  }

  AddTab(tname?) {
    this.tabDialog = this.dialog.open(TabDialogComponent, {
      width: "600px",
      panelClass: "db-dialog",
      data: {
        tabname: tname ? tname : "",
      },
    });

    this.tabDialog
      .afterClosed()
      .pipe(filter((name) => name))
      .subscribe((name) => {
        this.questionnaireService
          .getQuestionnaireContent(this.id)
          .subscribe(([model, savedTabs]) => {
            savedTabs.body.push({
              label: name,
              fields: [],
            });
            this.questionnaireService
              .saveQuestionSet(savedTabs, model, this.id)
              .subscribe((val: any) => {
                this.tabs = savedTabs.body;
                this.model = model;
                this.syncFormsWithTabs();
              });
          });
      });

    return false;
  }

  private insertField(fields: any[], question: any, insertAt?: InsertPosition) {
    const position = insertAt && insertAt.position;
    const anchorIndex =
      (position === "before" || position === "after") && insertAt.anchorId
        ? fields.findIndex((f) => f.id === insertAt.anchorId)
        : -1;

    if (anchorIndex === -1) {
      fields.push(question);
    } else {
      fields.splice(
        position === "before" ? anchorIndex : anchorIndex + 1,
        0,
        question,
      );
    }
  }

  AddField(tab, question, insertAt?: InsertPosition) {
    this.questionnaireService
      .getQuestionnaireContent(this.id)
      .subscribe(([model, savedTabs]) => {
        for (const t of savedTabs.body) {
          if (t.label === tab.label) {
            delete question.newField;
            delete question.label;

            if (question.type === "customcheckbox") {
              question.fieldGroup = [
                {
                  key: question.id + "_check",
                  id: question.id + "_check",
                  type: "checkbox",
                  className: "col-10",
                  templateOptions: {
                    required: true,
                    type: "checkbox",
                    label: question.checkboxLabel || "Include?",
                  },
                  isLockDownEdit: question.isLockDownEdit,
                },
                {
                  key: question.id + "_Text",
                  id: question.id + "_Text",
                  type: "customcheckbox",
                  className: "col-2",
                  templateOptions: {
                    label: "Text",
                  },
                  isLockDownEdit: question.isLockDownEdit,
                },
              ];
              delete question.type;
              delete question.key;
              question.wrappers = ["customcheckboxwrapper"];
              question.fieldGroupClassName = "row";
            }
            // Only the inner checkbox field stores this label; do not persist it on the parent.
            delete question.checkboxLabel;

            if (question.type === "customradio") {
              delete question.type;
              delete question.key;
              delete question.newField;
              question.wrappers = ["customradiowrapper"];
              question.fieldGroupClassName = "row";

              this.model[question.fieldGroup[0].key] =
                question.fieldGroup[0].templateOptions.options[0].value;
            }

            if (question.type === "customHealthAndSafetyResponsibilities") {
              ((question.templateOptions.name = Guid.newGuid()),
                (question.templateOptions.options = [
                  {
                    value: "Yes",
                    id: Guid.newGuid(),
                  },
                  {
                    value: "No",
                    id: Guid.newGuid(),
                  },
                ]));
            }

            question.hideExpression =
              '!((model.hideExpressionValue || "").split(";").some(e => e && field.sector && field.sector.indexOf(e) >= 0)) && ((model.hideExpressionValue || "").indexOf(field.id) < 0)';

            this.insertField(t.fields, question, insertAt);
            if (
              this.model.hideExpressionValue == null ||
              this.model.hideExpressionValue === undefined ||
              this.model.hideExpressionValue === question.sector
            ) {
              this.model.hideExpressionValue = question.sector;
            } else if (question.sector.split(";").length > 0) {
              for (let i = 0; i < question.sector.split(";").length; i++) {
                if (
                  this.model.hideExpressionValue.indexOf(
                    question.sector.split(";")[i],
                  ) < 0
                ) {
                  this.model.hideExpressionValue +=
                    ";" + question.sector.split(";")[i];
                }
              }
            } else if (
              this.model.hideExpressionValue.indexOf(question.sector) < 0
            ) {
              this.model.hideExpressionValue += ";" + question.sector;
            }
          }
        }
        this.questionnaireService
          .saveQuestionSet(savedTabs, this.model, this.id)
          .subscribe((val: any) => {
            this.tabs = savedTabs.body;
            this.model = this.model;
            this.syncFormsWithTabs();
          });
      });

    return false;
  }

  EditField(tab, question: Question) {
    if (question) {
      this.questionnaireService
        .getQuestionnaireContent(this.id)
        .subscribe(([model, savedTabs]) => {
          for (const t of savedTabs.body) {
            for (const f of t.fields) {
              if (f.id === question.id) {
                if (
                  f.hideExpression &&
                  f.hideExpression.indexOf("this.field") >= 0
                ) {
                  f.hideExpression =
                    '!((model.hideExpressionValue || "").split(";").some(e => e && field.sector && field.sector.indexOf(e) >= 0)) && ((model.hideExpressionValue || "").indexOf(field.id) < 0)';
                }

                f.sector = question.sector;
                f.tag = question.tag;
                f.templateOptions.label = question.templateOptions.label;
                f.templateOptions.required = question.templateOptions.required;
                f.templateOptions.multipleOutputs =
                  question.templateOptions.multipleOutputs;
                f.isLockDownEdit = question.isLockDownEdit;
                f.linkedHealthAndSafetyResponsibilitiesQuestion =
                  question.linkedHealthAndSafetyResponsibilitiesQuestion;

                if (f.fieldGroup) {
                  for (let i = 0; i < question.fieldGroup.length; i++) {
                    // The inner checkbox keeps its own label (set in the dialog);
                    // every other member mirrors the question text as before.
                    f.fieldGroup[i].templateOptions.label =
                      f.fieldGroup[i].type === "checkbox" &&
                      question.checkboxLabel
                        ? question.checkboxLabel
                        : question.templateOptions.label;
                    f.fieldGroup[i].isLockDownEdit = question.isLockDownEdit;
                  }
                }
              }
            }
          }

          if (
            this.model.hideExpressionValue == null ||
            this.model.hideExpressionValue === undefined ||
            this.model.hideExpressionValue === question.sector
          ) {
            this.model.hideExpressionValue = question.sector;
          } else if (question.sector.split(";").length > 0) {
            for (let i = 0; i < question.sector.split(";").length; i++) {
              if (
                this.model.hideExpressionValue.indexOf(
                  question.sector.split(";")[i],
                ) < 0
              ) {
                this.model.hideExpressionValue +=
                  ";" + question.sector.split(";")[i];
              }
            }
          } else if (
            this.model.hideExpressionValue.indexOf(question.sector) < 0
          ) {
            this.model.hideExpressionValue += ";" + question.sector;
          }

          this.questionnaireService
            .saveQuestionSet(savedTabs, this.model, this.id)
            .subscribe(() => {
              this.tabs = savedTabs.body;
              this.model = model;
              this.syncFormsWithTabs();
            });
        });
    }

    return false;
  }

  /***************************************************************************
   * Draft Documents
   **************************************************************************/
  configureDraftDocumentDataGrid() {
    this.draftDocumentGridConfig = {
      columnDefs: [
        {
          headerName: "Title",
          field: "title",
          minWidth: 350,
        },
        {
          headerName: "Created By",
          field: "createdBy",
          minWidth: 150,
        },
        {
          headerName: "Modified By",
          field: "modifiedBy",
          minWidth: 150,
        },
        {
          headerName: "Created",
          field: "createdDate",
          minWidth: 150,
        },
        {
          headerName: "Modified",
          field: "modifiedDate",
          minWidth: 150,
        },
        {
          headerName: "",
          field: "fileUrl",
          cellRenderer: "linkRenderer",
          cellRendererParams: {
            useParamsValue: true,
            linkTitle: "View / Edit",
          },
          minWidth: 150,
          suppressSorting: true,
          suppressFilter: true,
        },
      ],
      frameworkComponents: {
        linkRenderer: LinkRendererComponent,
      },
    };
  }

  initializeDraftDocumentRowData() {
    this.questionnaireService.getDraftDocuments(this.id).subscribe((data) => {
      this.draftDocumentRowData = data;
    });
  }

  openAddTemplateDialog() {
    this.addTemplateDialogRef = this.dialog.open(
      QuestionnaireAddTemplateDialogComponent,
      {
        width: "600px",
        panelClass: "db-dialog",
        data: {},
      },
    );

    this.addTemplateDialogRef
      .afterClosed()
      .pipe()
      .subscribe((newDraftDocument) => {
        if (newDraftDocument) {
          this.questionnaireService
            .addDraftDocument(newDraftDocument, this.id)
            .subscribe((response) => {
              this.initializeDraftDocumentRowData();
            });
        }
      });
  }

  showSector() {
    return environment.showSectors;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.subscriptionManager.unsubscribe();
    this.onDestroy$.next();
  }
}
